import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Timer, Clock, RefreshCw, Save, Package, FileSearch, Play, AlertCircle, Activity, Users, Truck, Receipt, RotateCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PharmeasyCronJobs = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [config, setConfig] = useState({
    unassigned_fetch_enabled: false,
    unassigned_fetch_interval_minutes: 30,
    detail_fetch_enabled: false,
    detail_fetch_interval_minutes: 5,
    detail_fetch_batch_size: 50,
    events_fetch_enabled: false,
    events_fetch_interval_minutes: 10,
    events_fetch_batch_size: 50,
    riders_sync_enabled: false,
    riders_sync_interval_hours: 24,
    trips_sync_enabled: false,
    trips_sync_interval_minutes: 15,
    trip_status_sync_enabled: false,
    trip_status_sync_interval_minutes: 15,
    ofd_events_fetch_enabled: false,
    ofd_events_interval_minutes: 5,
    ofd_events_start_hour_ist: 11,
    ofd_events_end_hour_ist: 23,
  });
  const [configMeta, setConfigMeta] = useState({ last_updated_at: null, last_updated_by: null });
  const [saving, setSaving] = useState(false);
  const [cronLogs, setCronLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [pendingDetailCount, setPendingDetailCount] = useState(0);
  const [pendingEventsCount, setPendingEventsCount] = useState(0);
  const [ridersCount, setRidersCount] = useState(0);
  const [tripsCount, setTripsCount] = useState(0);
  const [runningDetailSync, setRunningDetailSync] = useState(false);
  const [runningEventsSync, setRunningEventsSync] = useState(false);
  const [runningRidersSync, setRunningRidersSync] = useState(false);
  const [runningTripsSync, setRunningTripsSync] = useState(false);
  const [runningTripStatusSync, setRunningTripStatusSync] = useState(false);
  const [runningBillingCron, setRunningBillingCron] = useState(false);
  const [runningOfdEventsSync, setRunningOfdEventsSync] = useState(false);
  const [ofdCount, setOfdCount] = useState(0);
  const [billingSettings, setBillingSettings] = useState({ billing_cron_enabled: false, billing_cron_time: "00:30" });
  const [postponedResetConfig, setPostponedResetConfig] = useState({ postponed_reset_enabled: false, postponed_reset_hour_ist: 9, postponed_reset_minute_ist: 30 });
  const [savingPostponedReset, setSavingPostponedReset] = useState(false);
  const [runningPostponedReset, setRunningPostponedReset] = useState(false);
  const [activeTripsCount, setActiveTripsCount] = useState(0);
  const [logFilter, setLogFilter] = useState("");
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const logsPageSize = 20;

  useEffect(() => { 
    fetchConfig(); 
    fetchCronLogs(); 
    fetchPendingCounts();
    fetchBillingSettings();
    fetchPostponedResetConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/cron-config`);
      const { 
        unassigned_fetch_enabled, unassigned_fetch_interval_minutes, 
        detail_fetch_enabled, detail_fetch_interval_minutes, detail_fetch_batch_size,
        events_fetch_enabled, events_fetch_interval_minutes, events_fetch_batch_size,
        riders_sync_enabled, riders_sync_interval_hours,
        trips_sync_enabled, trips_sync_interval_minutes,
        trip_status_sync_enabled, trip_status_sync_interval_minutes,
        ofd_events_fetch_enabled, ofd_events_interval_minutes, ofd_events_start_hour_ist, ofd_events_end_hour_ist,
        last_updated_at, last_updated_by 
      } = res.data;
      setConfig({
        unassigned_fetch_enabled: unassigned_fetch_enabled || false,
        unassigned_fetch_interval_minutes: unassigned_fetch_interval_minutes || 30,
        detail_fetch_enabled: detail_fetch_enabled || false,
        detail_fetch_interval_minutes: detail_fetch_interval_minutes || 5,
        detail_fetch_batch_size: detail_fetch_batch_size || 50,
        events_fetch_enabled: events_fetch_enabled || false,
        events_fetch_interval_minutes: events_fetch_interval_minutes || 10,
        events_fetch_batch_size: events_fetch_batch_size || 50,
        riders_sync_enabled: riders_sync_enabled || false,
        riders_sync_interval_hours: riders_sync_interval_hours || 24,
        trips_sync_enabled: trips_sync_enabled || false,
        trips_sync_interval_minutes: trips_sync_interval_minutes || 15,
        trip_status_sync_enabled: trip_status_sync_enabled || false,
        trip_status_sync_interval_minutes: trip_status_sync_interval_minutes || 15,
        ofd_events_fetch_enabled: ofd_events_fetch_enabled || false,
        ofd_events_interval_minutes: ofd_events_interval_minutes || 5,
        ofd_events_start_hour_ist: ofd_events_start_hour_ist ?? 11,
        ofd_events_end_hour_ist: ofd_events_end_hour_ist ?? 23,
      });
      setConfigMeta({ last_updated_at, last_updated_by });
    } catch (e) { /* silent */ }
  };

  const fetchCronLogs = async (syncType = "", page = 1) => {
    setLoadingLogs(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        size: logsPageSize.toString(),
      });
      if (syncType) params.append("sync_type", syncType);
      
      const res = await axios.get(`${API}/pharmeasy-orders/cron-logs?${params}`);
      setCronLogs(res.data.data);
      setLogsTotal(res.data.total || 0);
      setLogsTotalPages(res.data.total_pages || 1);
    } catch (e) { /* silent */ }
    finally { setLoadingLogs(false); }
  };

  const fetchPendingCounts = async () => {
    try {
      const [detailRes, eventsRes, ridersRes, tripsRes, ofdRes] = await Promise.all([
        axios.get(`${API}/pharmeasy-orders/pending-details-count`),
        axios.get(`${API}/pharmeasy-orders/pending-events-count`),
        axios.get(`${API}/pharmeasy-orders/riders-count`),
        axios.get(`${API}/pharmeasy-orders/trips/stats`),
        axios.get(`${API}/pharmeasy-orders/ofd-shipments-count`),
      ]);
      setPendingDetailCount(detailRes.data.pending_count || 0);
      setPendingEventsCount(eventsRes.data.pending_count || 0);
      setRidersCount(ridersRes.data.count || 0);
      setTripsCount(tripsRes.data.total || 0);
      setOfdCount(ofdRes.data.ofd_count || 0);
      // Calculate active trips (not COMPLETED or RECON_FINISHED)
      const byStatus = tripsRes.data.by_status || {};
      const completedCount = byStatus["COMPLETED"]?.count || 0;
      const reconFinishedCount = byStatus["RECON_FINISHED"]?.count || 0;
      setActiveTripsCount((tripsRes.data.total || 0) - completedCount - reconFinishedCount);
    } catch (e) { /* silent */ }
  };

  const fetchBillingSettings = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-billing-settings/`);
      setBillingSettings({
        billing_cron_enabled: res.data.billing_cron_enabled || false,
        billing_cron_time: res.data.billing_cron_time || "00:30",
      });
    } catch (e) { /* silent */ }
  };

  const fetchPostponedResetConfig = async () => {
    try {
      const res = await axios.get(`${API}/pharmeasy-orders/postponed-reset-config`);
      setPostponedResetConfig({
        postponed_reset_enabled: res.data.postponed_reset_enabled || false,
        postponed_reset_hour_ist: res.data.postponed_reset_hour_ist ?? 9,
        postponed_reset_minute_ist: res.data.postponed_reset_minute_ist ?? 30,
      });
    } catch (e) { /* silent */ }
  };

  const handleSavePostponedReset = async () => {
    setSavingPostponedReset(true);
    try {
      await axios.put(`${API}/pharmeasy-orders/postponed-reset-config`, postponedResetConfig);
      toast.success("Postponed reset schedule saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSavingPostponedReset(false); }
  };

  const handleRunPostponedReset = async () => {
    setRunningPostponedReset(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/postponed-reset/run`);
      toast.success(res.data.message || `Reset ${res.data.total_reset} shipments`);
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run reset");
    } finally { setRunningPostponedReset(false); }
  };

  const handleManualBillingCron = async () => {
    setRunningBillingCron(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-billing/run-cron`);
      if (res.data.status === "success") {
        toast.success(`Billing calculated for ${res.data.date}: ${res.data.counts?.forward_delivered || 0} forward, ${res.data.counts?.return_dispatched || 0} return`);
      } else if (res.data.status === "skipped") {
        toast.info(res.data.reason || "Billing already calculated for yesterday");
      } else {
        toast.info("Billing cron completed");
      }
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to run billing cron");
    } finally { setRunningBillingCron(false); }
  };

  const handleSave = async () => {
    if (config.unassigned_fetch_interval_minutes < 5) {
      toast.error("Minimum interval for unassigned fetch is 5 minutes"); return;
    }
    if (config.detail_fetch_interval_minutes < 1) {
      toast.error("Minimum interval for detail fetch is 1 minute"); return;
    }
    if (config.events_fetch_interval_minutes < 1) {
      toast.error("Minimum interval for events fetch is 1 minute"); return;
    }
    if (config.riders_sync_interval_hours < 1) {
      toast.error("Minimum interval for riders sync is 1 hour"); return;
    }
    if (config.trips_sync_interval_minutes < 5) {
      toast.error("Minimum interval for trips sync is 5 minutes"); return;
    }
    if (config.trip_status_sync_interval_minutes < 5) {
      toast.error("Minimum interval for trip status sync is 5 minutes"); return;
    }
    if (config.detail_fetch_batch_size < 1 || config.detail_fetch_batch_size > 100) {
      toast.error("Detail batch size must be between 1 and 50"); return;
    }
    if (config.events_fetch_batch_size < 1 || config.events_fetch_batch_size > 100) {
      toast.error("Events batch size must be between 1 and 50"); return;
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/pharmeasy-orders/cron-config`, config);
      setConfigMeta({ last_updated_at: res.data.last_updated_at, last_updated_by: res.data.last_updated_by });
      toast.success("Cron job configuration saved successfully");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save cron config");
    } finally { setSaving(false); }
  };

  const handleManualDetailSync = async () => {
    setRunningDetailSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-details`);
      toast.success(`Detail sync complete: ${res.data.total_success} success, ${res.data.total_failed} failed`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to sync details");
    } finally { setRunningDetailSync(false); }
  };

  const handleManualEventsSync = async () => {
    setRunningEventsSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-events`);
      toast.success(`Events sync complete: ${res.data.total_success} success, ${res.data.total_delivered} delivered`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to sync events");
    } finally { setRunningEventsSync(false); }
  };

  const handleManualRidersSync = async () => {
    setRunningRidersSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-riders`);
      toast.success(`Riders sync complete: ${res.data.total_new} new, ${res.data.total_updated} updated`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to sync riders");
    } finally { setRunningRidersSync(false); }
  };

  const handleManualTripsSync = async () => {
    setRunningTripsSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/sync`);
      toast.success(`Trips sync complete: ${res.data.total_fetched} fetched, ${res.data.new_trips} new, ${res.data.updated_trips} updated`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to sync trips");
    } finally { setRunningTripsSync(false); }
  };

  const handleManualTripStatusSync = async () => {
    setRunningTripStatusSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/trips/sync-statuses`);
      toast.success(`Trip status sync complete: ${res.data.total_trips_processed} trips, ${res.data.total_shipments_updated} shipments updated`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to sync trip statuses");
    } finally { setRunningTripStatusSync(false); }
  };

  const handleManualOfdEventsSync = async () => {
    setRunningOfdEventsSync(true);
    try {
      const res = await axios.post(`${API}/pharmeasy-orders/sync-ofd-events`);
      toast.success(`OFD events fetch complete: ${res.data.total_processed} processed, ${res.data.total_success} success, ${res.data.total_delivered} delivered`);
      fetchPendingCounts();
      fetchCronLogs(logFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail?.error || err.response?.data?.detail || "Failed to fetch OFD events");
    } finally { setRunningOfdEventsSync(false); }
  };

  const handleLogFilterChange = (type) => {
    setLogFilter(type);
    setLogsPage(1);
    fetchCronLogs(type, 1);
  };

  const handleLogsPageChange = (newPage) => {
    setLogsPage(newPage);
    fetchCronLogs(logFilter, newPage);
  };

  const formatDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const getSyncTypeBadge = (syncType) => {
    switch (syncType) {
      case "detail_fetch": return <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">Detail</Badge>;
      case "events_fetch": return <Badge variant="outline" className="text-xs bg-purple-50 text-purple-700">Events</Badge>;
      case "riders_fetch": return <Badge variant="outline" className="text-xs bg-orange-50 text-orange-700">Riders</Badge>;
      case "trips_fetch": return <Badge variant="outline" className="text-xs bg-teal-50 text-teal-700">Trips</Badge>;
      case "trip_status_sync": return <Badge variant="outline" className="text-xs bg-cyan-50 text-cyan-700">Trip Status</Badge>;
      case "ofd_events_fetch": return <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700">OFD Events</Badge>;
      case "google_maps": return <Badge variant="outline" className="text-xs bg-red-50 text-red-700">Google Maps</Badge>;
      case "google_maps_geocode": return <Badge variant="outline" className="text-xs bg-red-50 text-red-700">Geocode</Badge>;
      case "google_maps_distance": return <Badge variant="outline" className="text-xs bg-rose-50 text-rose-700">Distance</Badge>;
      default: return <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">Unassigned</Badge>;
    }
  };

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-cron-jobs">
        <div>
          <h1 className="text-4xl font-bold text-slate-800 mb-2">Cron Jobs</h1>
          <p className="text-slate-600">Configure and monitor automated background tasks</p>
        </div>

        {/* Cron Config Card - Unassigned Fetch */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Package className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-slate-800">1. Fetch Unassigned Orders</h2>
                <p className="text-sm text-slate-500">Sync unassigned shipments list from Pharmeasy BOLT API</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.unassigned_fetch_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, unassigned_fetch_enabled: v })}
                  data-testid="unassigned-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.unassigned_fetch_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle the cron job on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="unassigned-interval">Interval (minutes)</Label>
                <Input
                  id="unassigned-interval"
                  type="number"
                  min={5}
                  value={config.unassigned_fetch_interval_minutes}
                  onChange={(e) => setConfig({ ...config, unassigned_fetch_interval_minutes: parseInt(e.target.value) || 5 })}
                  className="mt-1"
                  data-testid="unassigned-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 5 minutes</p>
              </div>
              <div></div>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Detail Fetch */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <FileSearch className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">2. Fetch Shipment Details</h2>
                  <p className="text-sm text-slate-500">Fetch detailed info (address, products, invoice) for each shipment</p>
                </div>
              </div>
              {pendingDetailCount > 0 && (
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                  <AlertCircle className="w-3.5 h-3.5 mr-1" />
                  {pendingDetailCount} pending
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.detail_fetch_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, detail_fetch_enabled: v })}
                  data-testid="detail-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.detail_fetch_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="detail-interval">Interval (minutes)</Label>
                <Input
                  id="detail-interval"
                  type="number"
                  min={1}
                  value={config.detail_fetch_interval_minutes}
                  onChange={(e) => setConfig({ ...config, detail_fetch_interval_minutes: parseInt(e.target.value) || 1 })}
                  className="mt-1"
                  data-testid="detail-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 1 minute</p>
              </div>

              <div>
                <Label htmlFor="detail-batch-size">Batch Size</Label>
                <Input
                  id="detail-batch-size"
                  type="number"
                  min={1}
                  max={50}
                  value={config.detail_fetch_batch_size}
                  onChange={(e) => setConfig({ ...config, detail_fetch_batch_size: parseInt(e.target.value) || 50 })}
                  className="mt-1"
                  data-testid="detail-cron-batch-input"
                />
                <p className="text-xs text-slate-400 mt-1">Shipments per run (1-50)</p>
              </div>

              <Button 
                onClick={handleManualDetailSync} 
                disabled={runningDetailSync || pendingDetailCount === 0}
                variant="outline"
                className="border-blue-200 text-blue-700 hover:bg-blue-50"
                data-testid="manual-detail-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningDetailSync ? "Running..." : "Run Now"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Events Fetch */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">3. Fetch Shipment Events</h2>
                  <p className="text-sm text-slate-500">Track shipment status until delivered (BOLT status updates)</p>
                </div>
              </div>
              {pendingEventsCount > 0 && (
                <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                  <AlertCircle className="w-3.5 h-3.5 mr-1" />
                  {pendingEventsCount} active
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.events_fetch_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, events_fetch_enabled: v })}
                  data-testid="events-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.events_fetch_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="events-interval">Interval (minutes)</Label>
                <Input
                  id="events-interval"
                  type="number"
                  min={1}
                  value={config.events_fetch_interval_minutes}
                  onChange={(e) => setConfig({ ...config, events_fetch_interval_minutes: parseInt(e.target.value) || 10 })}
                  className="mt-1"
                  data-testid="events-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 1 minute</p>
              </div>

              <div>
                <Label htmlFor="events-batch-size">Batch Size</Label>
                <Input
                  id="events-batch-size"
                  type="number"
                  min={1}
                  max={50}
                  value={config.events_fetch_batch_size}
                  onChange={(e) => setConfig({ ...config, events_fetch_batch_size: parseInt(e.target.value) || 50 })}
                  className="mt-1"
                  data-testid="events-cron-batch-input"
                />
                <p className="text-xs text-slate-400 mt-1">Shipments per run (1-50)</p>
              </div>

              <Button 
                onClick={handleManualEventsSync} 
                disabled={runningEventsSync || pendingEventsCount === 0}
                variant="outline"
                className="border-purple-200 text-purple-700 hover:bg-purple-50"
                data-testid="manual-events-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningEventsSync ? "Running..." : "Run Now"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Riders Sync */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-orange-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">4. Sync Riders</h2>
                  <p className="text-sm text-slate-500">Sync delivery riders (sprinters) from BOLT API and create user accounts</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">
                <Users className="w-3.5 h-3.5 mr-1" />
                {ridersCount} riders
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.riders_sync_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, riders_sync_enabled: v })}
                  data-testid="riders-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.riders_sync_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="riders-interval">Interval (hours)</Label>
                <Input
                  id="riders-interval"
                  type="number"
                  min={1}
                  value={config.riders_sync_interval_hours}
                  onChange={(e) => setConfig({ ...config, riders_sync_interval_hours: parseInt(e.target.value) || 24 })}
                  className="mt-1"
                  data-testid="riders-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 1 hour (default: 24h)</p>
              </div>

              <Button 
                onClick={handleManualRidersSync} 
                disabled={runningRidersSync}
                variant="outline"
                className="border-orange-200 text-orange-700 hover:bg-orange-50"
                data-testid="manual-riders-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningRidersSync ? "Syncing..." : "Sync Now"}
              </Button>
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> New riders will be created with:</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Email: <code className="bg-slate-200 px-1 rounded">{"{mobile}"}@edgistify.com</code></li>
                <li>Password: <code className="bg-slate-200 px-1 rounded">{"{mobile}"}</code></li>
                <li>Role: <code className="bg-slate-200 px-1 rounded">pharmeasy_rider</code></li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Trips Sync */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-teal-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">5. Sync Trips</h2>
                  <p className="text-sm text-slate-500">Sync delivery trips from BOLT API (last 30 days)</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">
                <Truck className="w-3.5 h-3.5 mr-1" />
                {tripsCount} trips
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.trips_sync_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, trips_sync_enabled: v })}
                  data-testid="trips-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.trips_sync_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="trips-interval">Interval (minutes)</Label>
                <Input
                  id="trips-interval"
                  type="number"
                  min={5}
                  value={config.trips_sync_interval_minutes}
                  onChange={(e) => setConfig({ ...config, trips_sync_interval_minutes: parseInt(e.target.value) || 15 })}
                  className="mt-1"
                  data-testid="trips-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 5 minutes (default: 15)</p>
              </div>

              <Button 
                onClick={handleManualTripsSync} 
                disabled={runningTripsSync}
                variant="outline"
                className="border-teal-200 text-teal-700 hover:bg-teal-50"
                data-testid="manual-trips-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningTripsSync ? "Syncing..." : "Sync Now"}
              </Button>
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> Trips are synced with agentId linked to local users via bolt_rider_id.</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>COMPLETED and RECON_FINISHED trips are not updated after initial sync</li>
                <li>Active trips (OUT_FOR_DELIVERY, RECON_STARTED, etc.) are updated on each sync</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Trip Status Sync */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center">
                  <Activity className="w-5 h-5 text-cyan-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">6. Sync Trip Shipment Statuses</h2>
                  <p className="text-sm text-slate-500">Update shipment statuses from active trips (excludes COMPLETED & RECON_FINISHED)</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-cyan-50 text-cyan-700 border-cyan-200">
                <Activity className="w-3.5 h-3.5 mr-1" />
                {activeTripsCount} active trips
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.trip_status_sync_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, trip_status_sync_enabled: v })}
                  data-testid="trip-status-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.trip_status_sync_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <Label htmlFor="trip-status-interval">Interval (minutes)</Label>
                <Input
                  id="trip-status-interval"
                  type="number"
                  min={5}
                  value={config.trip_status_sync_interval_minutes}
                  onChange={(e) => setConfig({ ...config, trip_status_sync_interval_minutes: parseInt(e.target.value) || 15 })}
                  className="mt-1"
                  data-testid="trip-status-cron-interval-input"
                />
                <p className="text-xs text-slate-400 mt-1">Minimum 5 minutes (default: 15)</p>
              </div>

              <Button 
                onClick={handleManualTripStatusSync} 
                disabled={runningTripStatusSync}
                variant="outline"
                className="border-cyan-200 text-cyan-700 hover:bg-cyan-50"
                data-testid="manual-trip-status-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningTripStatusSync ? "Syncing..." : "Sync Now"}
              </Button>
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> This job fetches task lists for all active trips and updates shipment statuses.</p>
              <ul className="list-disc list-inside mt-1 space-y-0.5">
                <li>Only processes trips where status is not COMPLETED or RECON_FINISHED</li>
                <li>Updates bolt_status and status fields on shipments</li>
                <li>Marks trip as COMPLETED when all tasks are DELIVERED</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - OFD Events Fetch */}
        <Card className="border-0 shadow-lg" data-testid="ofd-events-cron-card">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-rose-100 flex items-center justify-center">
                  <Truck className="w-5 h-5 text-rose-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">7. OFD Events Fetch</h2>
                  <p className="text-sm text-slate-500">Fetch events for Out for Delivery shipments every 5 min (11 AM - 11:59 PM IST)</p>
                </div>
              </div>
              <Badge variant="outline" className="bg-rose-50 text-rose-700 border-rose-200">
                <Truck className="w-3.5 h-3.5 mr-1" />
                {ofdCount} OFD
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="flex items-center gap-4">
                <Switch
                  checked={config.ofd_events_fetch_enabled}
                  onCheckedChange={(v) => setConfig({ ...config, ofd_events_fetch_enabled: v })}
                  data-testid="ofd-events-cron-enabled-switch"
                />
                <div>
                  <p className="text-sm font-medium text-slate-700">
                    {config.ofd_events_fetch_enabled ? "Enabled" : "Disabled"}
                  </p>
                  <p className="text-xs text-slate-400">Toggle on/off</p>
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Interval (minutes)</label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={config.ofd_events_interval_minutes}
                  onChange={(e) => setConfig({ ...config, ofd_events_interval_minutes: parseInt(e.target.value) || 5 })}
                  className="h-9"
                  data-testid="ofd-events-interval-input"
                />
              </div>

              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs font-medium text-slate-600 mb-1 block">Start (IST)</label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={config.ofd_events_start_hour_ist}
                    onChange={(e) => setConfig({ ...config, ofd_events_start_hour_ist: parseInt(e.target.value) ?? 11 })}
                    className="h-9"
                    data-testid="ofd-events-start-hour-input"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-slate-600 mb-1 block">End (IST)</label>
                  <Input
                    type="number"
                    min={0}
                    max={23}
                    value={config.ofd_events_end_hour_ist}
                    onChange={(e) => setConfig({ ...config, ofd_events_end_hour_ist: parseInt(e.target.value) ?? 23 })}
                    className="h-9"
                    data-testid="ofd-events-end-hour-input"
                  />
                </div>
              </div>

              <Button 
                onClick={handleManualOfdEventsSync} 
                disabled={runningOfdEventsSync || ofdCount === 0}
                variant="outline"
                className="border-rose-200 text-rose-700 hover:bg-rose-50"
                data-testid="manual-ofd-events-sync-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningOfdEventsSync ? "Running..." : "Run Now"}
              </Button>
            </div>

            <div className="mt-4 p-4 bg-slate-50 rounded-lg text-xs text-slate-600">
              <p><strong>Note:</strong> This is a dedicated high-frequency cron for OFD shipments only. It runs independently of the general "Fetch Shipment Events" cron (#3).</p>
            </div>
          </CardContent>
        </Card>

        {/* Cron Config Card - Billing */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">8. Daily Billing Calculation</h2>
                  <p className="text-sm text-slate-500">Calculate Forward Delivered & Return Dispatched counts for previous day</p>
                </div>
              </div>
              <Badge variant="outline" className={billingSettings.billing_cron_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500"}>
                {billingSettings.billing_cron_enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-end">
              <div className="flex items-center gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm font-medium text-slate-700">
                    Status: {billingSettings.billing_cron_enabled ? "Active" : "Inactive"}
                  </p>
                  <p className="text-xs text-slate-400">Runs daily at {billingSettings.billing_cron_time} IST</p>
                </div>
              </div>

              <div>
                <Button 
                  variant="outline"
                  onClick={() => navigate('/pharmeasy/billing')}
                  className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                  data-testid="billing-settings-link-btn"
                >
                  Configure Billing Settings
                </Button>
              </div>

              <Button 
                onClick={handleManualBillingCron} 
                disabled={runningBillingCron}
                variant="outline"
                className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                data-testid="manual-billing-cron-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningBillingCron ? "Running..." : "Run Now"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* 9. Postponed Status Reset Cron */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">9. Postponed Status Reset</h2>
                  <p className="text-sm text-slate-500">Auto-reset Postponed shipments (INWARD_PLANNED / INWARDED) to Unassigned daily</p>
                </div>
              </div>
              <Badge variant="outline" className={postponedResetConfig.postponed_reset_enabled ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-50 text-slate-500"}>
                {postponedResetConfig.postponed_reset_enabled ? "Enabled" : "Disabled"}
              </Badge>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
              <div className="flex items-center gap-3">
                <Switch
                  checked={postponedResetConfig.postponed_reset_enabled}
                  onCheckedChange={(val) => setPostponedResetConfig(prev => ({ ...prev, postponed_reset_enabled: val }))}
                  data-testid="postponed-reset-enabled-switch"
                />
                <Label className="text-sm text-slate-700">Enable Daily Reset</Label>
              </div>

              <div className="space-y-1">
                <Label className="text-xs text-slate-500">Time (IST)</Label>
                <Input
                  type="time"
                  value={`${String(postponedResetConfig.postponed_reset_hour_ist).padStart(2, '0')}:${String(postponedResetConfig.postponed_reset_minute_ist).padStart(2, '0')}`}
                  onChange={(e) => {
                    const [h, m] = e.target.value.split(':').map(Number);
                    setPostponedResetConfig(prev => ({ ...prev, postponed_reset_hour_ist: h, postponed_reset_minute_ist: m }));
                  }}
                  data-testid="postponed-reset-time-input"
                  className="w-32"
                />
              </div>

              <Button
                variant="outline"
                onClick={handleSavePostponedReset}
                disabled={savingPostponedReset}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="postponed-reset-save-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingPostponedReset ? "Saving..." : "Save Schedule"}
              </Button>

              <Button
                variant="outline"
                onClick={handleRunPostponedReset}
                disabled={runningPostponedReset}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
                data-testid="postponed-reset-run-btn"
              >
                <Play className="w-4 h-4 mr-2" />
                {runningPostponedReset ? "Running..." : "Run Now"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: "#1B8C3D" }} data-testid="cron-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save All Configuration"}
          </Button>
        </div>

        {configMeta.last_updated_at && (
          <div className="text-xs text-slate-400 text-right">
            Last updated: {formatDate(configMeta.last_updated_at)} by {configMeta.last_updated_by || "—"}
          </div>
        )}

        {/* Recent Execution Logs */}
        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" /> Recent Sync Logs
              </h2>
              <div className="flex items-center gap-3">
                <select
                  value={logFilter}
                  onChange={(e) => handleLogFilterChange(e.target.value)}
                  className="text-sm border rounded-md px-2 py-1 text-slate-600"
                  data-testid="log-filter-select"
                >
                  <option value="">All Types</option>
                  <option value="unassigned_shipments">Unassigned Fetch</option>
                  <option value="detail_fetch">Detail Fetch</option>
                  <option value="events_fetch">Events Fetch</option>
                  <option value="riders_fetch">Riders Sync</option>
                  <option value="trips_fetch">Trips Sync</option>
                  <option value="trip_status_sync">Trip Status Sync</option>
                  <option value="ofd_events_fetch">OFD Events Fetch</option>
                  <option value="postponed_status_reset">Postponed Status Reset</option>
                </select>
                <Button variant="outline" size="sm" onClick={() => fetchCronLogs(logFilter, logsPage)}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
                </Button>
              </div>
            </div>

            {loadingLogs ? (
              <p className="text-sm text-slate-500 py-4">Loading...</p>
            ) : cronLogs.length === 0 ? (
              <div className="text-center py-8">
                <Timer className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No sync logs yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Completed</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Triggered By</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Fetched</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">New</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Updated</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {cronLogs.map((log, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-600 whitespace-nowrap">
                          <div>{formatDate(log.completed_at).split(', ')[0]}</div>
                          <div className="text-xs text-slate-500">{formatDate(log.completed_at).split(', ')[1]}</div>
                        </td>
                        <td className="px-4 py-3 align-middle">{getSyncTypeBadge(log.sync_type)}</td>
                        <td className="px-4 py-3 align-middle">
                          <Badge variant="outline" className={`text-xs ${log.triggered_by === "cron" ? "bg-slate-100 text-slate-700" : "bg-amber-50 text-amber-700"}`}>
                            {log.triggered_by || "—"}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle text-right text-sm font-medium text-slate-800">
                          {log.total_fetched ?? log.total_processed ?? "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-emerald-600 font-medium">
                          {log.total_new ?? log.total_success ?? "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-right text-sm text-blue-600 font-medium">
                          {log.total_updated ?? log.total_failed ?? "—"}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-400">
                          {log.sync_type === "events_fetch" && log.total_delivered !== undefined
                            ? `${log.total_delivered} delivered`
                            : log.batch_size 
                            ? `Batch: ${log.batch_size}` 
                            : log.date_range 
                            ? `${log.date_range.start} → ${log.date_range.end}` 
                            : "—"
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                
                {/* Pagination */}
                {logsTotalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
                    <p className="text-sm text-slate-500">
                      Showing {(logsPage - 1) * logsPageSize + 1}–{Math.min(logsPage * logsPageSize, logsTotal)} of {logsTotal}
                    </p>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={logsPage <= 1} 
                        onClick={() => handleLogsPageChange(logsPage - 1)}
                      >
                        Previous
                      </Button>
                      <span className="text-sm text-slate-600 px-2">
                        Page {logsPage} of {logsTotalPages}
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        disabled={logsPage >= logsTotalPages} 
                        onClick={() => handleLogsPageChange(logsPage + 1)}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PharmeasyLayout>
  );
};

export default PharmeasyCronJobs;
